// ** React Imports
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// ** Third Party Imports
import { isEmpty, orderBy } from 'lodash';
import Tabs, { TabPane } from 'rc-tabs';
import { SkeletonLoading, toaster } from '@saleshandy/design-system';
import { useHistory } from 'react-router';
// ** Shared Imports
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import { calculateVisibleChipsCount } from '../../../../../../shared/functions/calculate-visible-chips';
import { Plus } from '../../../../../../shared/svg/plus';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../shared/utils';
// ** Custom Imports
import { PinUnpinActionType, SavedFilter } from '../../../../types';
import ProspectFiltersModal from '../../../prospect-filters-modal';
import AllProspectChip from './components/all-prospect-chip';
import SavedFilterChip from './components/saved-filter-chip/saved-filter-chip-container';
import ViewList from './components/view-list';
import type { IProps } from './saved-filters-container';
import { getSavedViewDefaultClassNames } from './utils/helper';
import { getDefaultFilterGroupObject } from '../../../prospect-filters-modal/utils/get-default-filter-group-object';

const ViewTabsSkeleton = () => (
  <div className="d-flex align-items-center gap-24">
    <SkeletonLoading width={90} height={15} />
    <SkeletonLoading width={90} height={15} />
    <SkeletonLoading width={90} height={15} />
    <SkeletonLoading width={90} height={15} />
    <SkeletonLoading width={90} height={15} />
  </div>
);

/**
 *
 * @param filters - The filters to be applied to the prospect list
 * @param savedFilters - The saved filters to be displayed in the saved filters list
 * @param fetchProspectListAndCount - The function to fetch the prospect list and count
 * @param resetFilterGroupLogic - The function to reset the filter group logic
 * @param prospectCount - The count of prospects in the prospect list
 * @param getProspectListCountRequestStatus - The status of the prospect list count request
 * @param sendGetContactListByViewIdRequest - The function to send the contact list by view id request
 * @param sendGetProspectListCountRequest - The function to send the prospect list count request
 * @param sendPinUnpinSavedFilterRequest - The function to send the pin unpin saved filter request
 * @returns
 */
const SavedFilters: React.FC<IProps> = ({
  filters,
  savedFilters,
  fetchProspectListAndCount,
  resetFilterGroupLogic,

  prospectCount,
  // prospectKanbanCount,
  getProspectListCountRequestStatus,

  sendGetContactListByViewIdRequest,
  sendGetProspectListCountRequest,
  sendPinUnpinSavedFilterRequest,
  pinUnpinSavedFilterRequestStatus,

  selectedSavedView,
  setSelectedSavedView,
  removedSelectedSavedView,

  getFilterViewListRequestStatus,
  activeViewId,
  setProspectViewIdFilter,
  getFilterViewListRequest,
}) => {
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const viewId = urlParams.get('viewId');

  const containerRef = useRef(null);
  const [visiblePinnedSavedViews, setVisiblePinnedSavedViews] = useState([]);
  const [isViewModalVisible, setViewModalVisible] = useState(false);
  const [selectedView, setSelectedView] = useState<number>(null);

  const [
    visiblePinnedSavedViewsCount,
    setVisiblePinnedSavedViewsCount,
  ] = useState(0);
  const [pinUnpinAction, setPinUnpinAction] = useState<PinUnpinActionType>(
    null,
  );
  const [previousViewId, setPreviousViewId] = useState<string | null>(null);

  //* Helper Functions */
  const getSortedPinnedSavedViews = (views: SavedFilter[]) =>
    orderBy(
      views,
      [
        (item) => item.pinnedAt !== null,
        (item) => (item.pinnedAt ? new Date(item.pinnedAt) : 0),
      ],
      ['desc', 'desc'],
    );

  const checkAndAddSelectedSavedView = (filter: SavedFilter) => {
    if (
      visiblePinnedSavedViews?.length > 0 &&
      visiblePinnedSavedViewsCount > 0
    ) {
      const isSelectedSavedViewAlreadyVisible = visiblePinnedSavedViews.find(
        (obj) => obj.id === filter.id,
      );
      if (!isSelectedSavedViewAlreadyVisible) {
        const visiblePinnedSavedViewsList = getSortedPinnedSavedViews(
          visiblePinnedSavedViews,
        );

        const lastSavedView =
          visiblePinnedSavedViews[visiblePinnedSavedViews?.length - 1];
        if (
          visiblePinnedSavedViewsCount === visiblePinnedSavedViews?.length ||
          lastSavedView?.pinnedAt === null
        ) {
          visiblePinnedSavedViewsList.pop();
          visiblePinnedSavedViewsList.push(filter);
          setVisiblePinnedSavedViews(visiblePinnedSavedViewsList);
        } else {
          visiblePinnedSavedViewsList.push(filter);
          setVisiblePinnedSavedViews(visiblePinnedSavedViewsList);
        }
      }
    }
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
  };

  const getViewModalHeaderTitle = () => {
    const date = new Date();

    const ordinal = (n) => {
      if (n % 100 >= 11 && n % 100 <= 13) {
        return `${n}th`;
      }
      switch (n % 10) {
        case 1:
          return `${n}st`;
        case 2:
          return `${n}nd`;
        case 3:
          return `${n}rd`;
        default:
          return `${n}th`;
      }
    };

    const day = ordinal(date.getDate());
    const month = date.toLocaleString('en-US', { month: 'short' });
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    return `New View - ${day} ${month} | ${time}`;
  };

  const handlePinUnpin = (viewId: number, action: PinUnpinActionType) => {
    sendPinUnpinSavedFilterRequest(viewId, action);
  };

  const isViewsListLoading = useMemo(
    () => getIsRequestPending(getFilterViewListRequestStatus),
    [getFilterViewListRequestStatus],
  );

  const onFilterApplied = (filter: SavedFilter) => {
    const selectedFields = filters?.selectedFields || [];

    setSelectedSavedView(filter);
    setSelectedView(filter?.id);

    const currentPath = window?.location?.pathname;
    // Only call list APIs for list view paths, explicitly exclude kanban paths
    const isKanbanPath = currentPath?.includes('/crm/kanban');
    if (
      !isKanbanPath &&
      (currentPath === '/crm' ||
        currentPath === '/crm/' ||
        currentPath === '/crm/list' ||
        currentPath === '/crm/list/')
    ) {
      // For getting list view details, For kanaban logic to get details is inside it
      sendGetContactListByViewIdRequest({
        viewId: filter.id,
        selectedField: selectedFields.map((f) => f.id.toString()),
      });
      sendGetProspectListCountRequest({
        viewId: filter.id,
        selectedField: selectedFields.map((f) => f.id.toString()),
      });
    }

    history.push({
      search: `viewId=${filter.id}`,
    });
  };

  const onRemoveFilter = (isFromAllProspect = false) => {
    removedSelectedSavedView();
    resetFilterGroupLogic();
    fetchProspectListAndCount({
      doNoSendFilterGroupLogic: true,
      forceFetch: true,
    });

    if (!isFromAllProspect) {
      handlePinUnpin(selectedSavedView?.id, PinUnpinActionType.UnPin);
    }

    history.push({
      search: '',
    });
  };

  const updateVisibleChipsCount = useCallback(() => {
    if (containerRef?.current) {
      setVisiblePinnedSavedViewsCount(
        calculateVisibleChipsCount({
          containerElement: containerRef.current,
          chipWidth: 164,
          plusNWidth: 172 + 72, // all prospect chip width + view more chip width
        }),
      );
    }
  }, [containerRef]);

  useEffect(() => {
    updateVisibleChipsCount();
    window.addEventListener('resize', updateVisibleChipsCount);

    return () => window.removeEventListener('resize', updateVisibleChipsCount);
  }, [updateVisibleChipsCount]);

  useEffect(() => {
    updateVisibleChipsCount();
  }, [updateVisibleChipsCount, savedFilters, selectedSavedView]);

  useEffect(() => {
    // Only set to null when viewId changes, not on initial render
    if (viewId && previousViewId !== null && previousViewId !== viewId) {
      setProspectViewIdFilter(null);
    }
    // Update previousViewId for next comparison
    setPreviousViewId(viewId);
  }, [viewId, previousViewId, setProspectViewIdFilter]);

  useEffect(() => {
    if (savedFilters?.length !== 0 && visiblePinnedSavedViewsCount > 0) {
      const pinnedOrSelectedSavedViews =
        savedFilters?.filter(
          (filter) =>
            filter.filterName !== 'All Prospects' &&
            (!isEmpty(filter.pinnedAt) || filter.id === selectedSavedView?.id),
        ) || [];

      const sortedPinnedSavedViews = getSortedPinnedSavedViews(
        pinnedOrSelectedSavedViews,
      );

      const visibleViews: SavedFilter[] = [];
      // Only show pinned or currently selected views
      sortedPinnedSavedViews.forEach((view) => {
        if (visibleViews.length < visiblePinnedSavedViewsCount) {
          visibleViews.push(view);
        }
      });

      setVisiblePinnedSavedViews(visibleViews);
    }
  }, [savedFilters, visiblePinnedSavedViewsCount]);

  useEffect(() => {
    // Get viewId from queryURL
    // const urlParams = new URLSearchParams(window.location.search);
    // const viewId = urlParams.get('viewId');

    if (viewId) {
      const filter = savedFilters.find((f) => f.id === Number(viewId));

      if (filter) {
        onFilterApplied(filter);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedSavedView) {
      checkAndAddSelectedSavedView(selectedSavedView);
    }
  }, [selectedSavedView]);

  useEffect(() => {
    executeOnRequestStatus({
      status: pinUnpinSavedFilterRequestStatus,
      onSuccess: () => {
        if (pinUnpinAction === PinUnpinActionType.UnPin) {
          onRemoveFilter(true);
          history.push({
            search: '',
          });
        }
        if (pinUnpinAction === PinUnpinActionType.Pin) {
          toaster?.success('View Pinned Successfully');
        }
        setPinUnpinAction(null);
      },
    });
  }, [pinUnpinSavedFilterRequestStatus, pinUnpinAction]);

  useEffect(() => {
    if (!selectedSavedView) {
      setSelectedView(null);
    } else {
      setSelectedView(selectedSavedView?.id);
    }
  }, [selectedSavedView]);

  useEffect(() => {
    if (savedFilters && savedFilters?.length > 0) {
      // const params = new URLSearchParams(window.location.search);
      // const paramCount = Array.from(params.keys())?.length;
      // const hasOnlyViewId = paramCount === 1 && params.has('viewId');

      if (!selectedSavedView || !selectedView) {
        // if (hasOnlyViewId) {
        // history.push({
        //   search: '',
        // });
        // }
      } else if (!viewId && (selectedSavedView || selectedView)) {
        history.push({
          search: `viewId=${selectedView}`,
        });
      }
    }
  }, [selectedSavedView, viewId, savedFilters, selectedView]);

  const isCountLoading = getIsRequestPending(getProspectListCountRequestStatus);

  const formattedVisiblePinnedSavedViews = useMemo(() => {
    const filteredViews = visiblePinnedSavedViews?.filter(
      (filter) => filter?.filterName !== 'All Prospects',
    );
    return filteredViews?.length > 0 ? filteredViews : [];
  }, [visiblePinnedSavedViews]);

  const activeKey = selectedSavedView?.id
    ? `filter-${selectedSavedView.id}`
    : 'all-prospects';

  if (isViewsListLoading) {
    return <ViewTabsSkeleton />;
  }

  return (
    <div ref={containerRef} className="d-flex saved-filters__items">
      <div ref={containerRef} className="saved-filters__items">
        <Tabs
          activeKey={activeKey}
          prefixCls="bs-tabs"
          className="bs-tabs-small saved-filters__tabs"
        >
          {savedFilters?.length > 0 && (
            <TabPane
              tab={
                <AllProspectChip
                  selectedSavedViewId={selectedSavedView?.id}
                  label="All Prospects"
                  onRemoveFilter={() => onRemoveFilter(true)}
                  isCountLoading={isCountLoading}
                  prospectCount={prospectCount}
                />
              }
              key="all-prospects"
            />
          )}

          {formattedVisiblePinnedSavedViews?.map((filter) => (
            <TabPane
              key={`filter-${filter.id}`}
              tab={
                <SavedFilterChip
                  selectedSavedViewId={selectedSavedView?.id}
                  filterId={filter.id}
                  label={filter.filterName}
                  onFilterApplied={() => {
                    onFilterApplied(filter);
                  }}
                  onRemoveFilter={onRemoveFilter}
                  isCountLoading={isCountLoading}
                  prospectCount={prospectCount}
                  filter={filter}
                  selectedSavedView={selectedSavedView}
                  handlePinUnpin={(viewId, action) => {
                    handlePinUnpin(viewId, action);
                    setPinUnpinAction(action);
                  }}
                />
              }
            />
          ))}
        </Tabs>

        <div className={`${getSavedViewDefaultClassNames()} p-0`}>
          <ViewList
            handlePinUnpin={(viewId, action) => {
              handlePinUnpin(viewId, action);
              setPinUnpinAction(action);
            }}
            onFilterApplied={onFilterApplied}
            onSelectAllProspects={() => onRemoveFilter(true)}
            selectedViewId={selectedSavedView?.id}
            pinnedFilters={formattedVisiblePinnedSavedViews?.length}
          />
        </div>

        <div
          className="plus-icon-container"
          {...accessibleOnClick(() => {
            setViewModalVisible(true);
            getDefaultFilterGroupObject(1);
          })}
        >
          <OverlayTooltip text="Add View">
            <div className="plus-icon-container-tooltip">
              <Plus width={16} height={16} className="plus-icon" />
            </div>
          </OverlayTooltip>
        </div>
        <ProspectFiltersModal
          show={isViewModalVisible}
          onClose={handleCloseViewModal}
          headerTitle={getViewModalHeaderTitle()}
          isCreateViewModal
        />
      </div>
    </div>
  );
};

export default memo(SavedFilters);
